import { Injectable } from '@angular/core';
import type { ITableChange } from '@vinnova/ui';
import type {
  Andringsmarkering,
  BudgetMedAndringar,
  ProjektpartMedAndringar,
} from '../../components/question/budget/budget.interface';

@Injectable({
  providedIn: 'root',
})
export class BudgetTransformService {
  /**
   * Transformerar budget ändringsmarkeringar från backend's hierarkiska struktur
   * till FormRenderer's platta array-format för total-tabellen OCH projektparter.
   *
   * @param budgetMedAndring - Budget data från backend
   * @returns Object med kostnaderTotalChanges och finansieringTotalChanges
   */
  transformBudgetChanges(budgetMedAndring: BudgetMedAndringar): {
    kostnaderTotalChanges: ITableChange[];
    finansieringTotalChanges: ITableChange[];
  } {
    const kostnaderTotalChanges: ITableChange[] = [];
    const finansieringTotalChanges: ITableChange[] = [];

    // Transformera projektparter ändringsmarkeringar
    if (budgetMedAndring.projektparter) {
      for (const projektpart of budgetMedAndring.projektparter) {
        projektpart.kostnaderChanges =
          this.buildProjektpartKostnaderChanges(projektpart);
        projektpart.finansieringChanges =
          this.buildProjektpartFinansieringChanges(projektpart);
      }
    }

    // Kostnader per kostnadsslag och år
    if (budgetMedAndring.kostnaderTotal) {
      for (const kostnad of budgetMedAndring.kostnaderTotal) {
        for (const kostnadPerAr of kostnad.kostnaderPerAr) {
          this.addAndringsmarkeringar(
            kostnadPerAr.andringsmarkeringar,
            kostnaderTotalChanges,
            kostnad.kostnadsslag.toString(),
            kostnadPerAr.ar.toString(),
          );
        }
      }
    }

    // Summa kostnader per år
    if (budgetMedAndring.summaKostnaderTotal) {
      for (const kostnad of budgetMedAndring.summaKostnaderTotal) {
        this.addAndringsmarkeringar(
          kostnad.andringsmarkeringar,
          kostnaderTotalChanges,
          'total',
          kostnad.ar.toString(),
        );
      }
    }

    // Kostnader aggregerat per kostnadsslag (summa per rad)
    if (budgetMedAndring.kostnaderAggregeratPerKostnadsslagTotal) {
      for (const kostnad of budgetMedAndring.kostnaderAggregeratPerKostnadsslagTotal) {
        this.addAndringsmarkeringar(
          kostnad.andringsmarkeringar,
          kostnaderTotalChanges,
          kostnad.typ.toString(),
          'total',
        );
      }
    }

    // Grand total kostnader
    if (budgetMedAndring.kostnaderTotalt?.andringsmarkeringar) {
      this.addAndringsmarkeringar(
        budgetMedAndring.kostnaderTotalt.andringsmarkeringar,
        kostnaderTotalChanges,
        'total',
        'total',
      );
    }

    // Bidrag Vinnova per år
    if (budgetMedAndring.bidragVinnovaTotalt) {
      for (const bidrag of budgetMedAndring.bidragVinnovaTotalt) {
        this.addAndringsmarkeringar(
          bidrag.andringsmarkeringar,
          finansieringTotalChanges,
          '1',
          bidrag.ar.toString(),
        );
      }
    }

    // Andra finansiärer per år
    if (budgetMedAndring.andraFinansiarerTotal) {
      for (const finansiering of budgetMedAndring.andraFinansiarerTotal) {
        this.addAndringsmarkeringar(
          finansiering.andringsmarkeringar,
          finansieringTotalChanges,
          '2',
          finansiering.ar.toString(),
        );
      }
    }

    // Övriga finansiärer totalt
    if (budgetMedAndring.övrigaFinansiarerTotalt?.andringsmarkeringar) {
      this.addAndringsmarkeringar(
        budgetMedAndring.övrigaFinansiarerTotalt.andringsmarkeringar,
        finansieringTotalChanges,
        '2',
        'total',
      );
    }

    // Egen finansiering per år
    if (budgetMedAndring.egenFinansieringTotalt) {
      for (const egenFinansiering of budgetMedAndring.egenFinansieringTotalt) {
        this.addAndringsmarkeringar(
          egenFinansiering.andringsmarkeringar,
          finansieringTotalChanges,
          '3',
          egenFinansiering.ar.toString(),
        );
      }
    }

    // Total egen finansiering
    if (budgetMedAndring.totalEgenFinansieringTotalt?.andringsmarkeringar) {
      this.addAndringsmarkeringar(
        budgetMedAndring.totalEgenFinansieringTotalt.andringsmarkeringar,
        finansieringTotalChanges,
        '3',
        'total',
      );
    }

    // Total finansiering per år
    if (budgetMedAndring.finansieringTotalt) {
      for (const finansiering of budgetMedAndring.finansieringTotalt) {
        this.addAndringsmarkeringar(
          finansiering.andringsmarkeringar,
          finansieringTotalChanges,
          'total',
          finansiering.ar.toString(),
        );
      }
    }

    // Total bidrag Vinnova
    if (budgetMedAndring.totalBidragVinnovaTotalt?.andringsmarkeringar) {
      this.addAndringsmarkeringar(
        budgetMedAndring.totalBidragVinnovaTotalt.andringsmarkeringar,
        finansieringTotalChanges,
        '1',
        'total',
      );
    }

    // Grand total finansiering
    if (budgetMedAndring.totalFinansieringTotalt?.andringsmarkeringar) {
      this.addAndringsmarkeringar(
        budgetMedAndring.totalFinansieringTotalt.andringsmarkeringar,
        finansieringTotalChanges,
        'total',
        'total',
      );
    }

    return {
      kostnaderTotalChanges,
      finansieringTotalChanges,
    };
  }

  /**
   * Helper för att lägga till ändringsmarkeringar från backend till target-arrayen
   */
  private addAndringsmarkeringar(
    andringsmarkeringar: Andringsmarkering[] | undefined,
    targetArray: ITableChange[],
    rowId: string,
    columnId: string,
  ): void {
    if (!andringsmarkeringar || andringsmarkeringar.length === 0) {
      return;
    }

    for (const andring of andringsmarkeringar) {
      targetArray.push({
        rowId,
        columnId,
        order: andring.ordning,
        text: andring.text,
        textEn: andring.textEn,
        value: andring.vardeDecimal ?? (parseFloat(andring.varde) || 0),
        date:
          typeof andring.datum === 'string'
            ? new Date(andring.datum)
            : andring.datum,
      });
    }
  }

  /**
   * Bygger kostnaderChanges array för en projektpart från hierarchisk struktur
   */
  private buildProjektpartKostnaderChanges(
    projektpart: ProjektpartMedAndringar,
  ): ITableChange[] {
    const changes: ITableChange[] = [];

    // Individuella kostnader per kostnadsslag och år
    if (projektpart.kostnader) {
      for (const kostnad of projektpart.kostnader) {
        for (const kostnadPerAr of kostnad.kostnaderPerAr) {
          this.addAndringsmarkeringar(
            kostnadPerAr.andringsmarkeringar,
            changes,
            kostnad.kostnadsslag.toString(),
            kostnadPerAr.ar.toString(),
          );
        }
      }
    }

    // Summa kostnader per år (rowId='total', columnId=år)
    if (projektpart.summaKostnader) {
      for (const kostnad of projektpart.summaKostnader) {
        this.addAndringsmarkeringar(
          kostnad.andringsmarkeringar,
          changes,
          'total',
          kostnad.ar.toString(),
        );
      }
    }

    // Kostnader aggregerat per kostnadsslag (rowId=typ, columnId='total')
    if (projektpart.kostnaderAggregeratPerKostnadsslag) {
      for (const kostnad of projektpart.kostnaderAggregeratPerKostnadsslag) {
        this.addAndringsmarkeringar(
          kostnad.andringsmarkeringar,
          changes,
          kostnad.typ.toString(),
          'total',
        );
      }
    }

    // Grand total kostnader (rowId='total', columnId='total')
    if (projektpart.kostnaderTotalt) {
      this.addAndringsmarkeringar(
        projektpart.kostnaderTotalt.andringsmarkeringar,
        changes,
        'total',
        'total',
      );
    }

    return changes;
  }

  /**
   * Bygger finansieringChanges array för en projektpart från hierarchisk struktur
   */
  private buildProjektpartFinansieringChanges(
    projektpart: ProjektpartMedAndringar,
  ): ITableChange[] {
    const changes: ITableChange[] = [];

    // Bidrag Vinnova per år (rowId='1', columnId=år)
    if (projektpart.bidragPerAr) {
      for (const bidrag of projektpart.bidragPerAr) {
        this.addAndringsmarkeringar(
          bidrag.andringsmarkeringar,
          changes,
          '1',
          bidrag.ar.toString(),
        );
      }
    }

    // Andra finansiärer per år och totalt
    if (projektpart.finansiarer) {
      for (const finansiar of projektpart.finansiarer) {
        // Per år
        for (const finansieringPerAr of finansiar.finansieringPerAr) {
          this.addAndringsmarkeringar(
            finansieringPerAr.andringsmarkeringar,
            changes,
            finansiar.id,
            finansieringPerAr.ar.toString(),
          );
        }
        // Totalt per finansiär
        if (finansiar.finansieringTotalt) {
          this.addAndringsmarkeringar(
            finansiar.finansieringTotalt.andringsmarkeringar,
            changes,
            finansiar.id,
            'total',
          );
        }
      }
    }

    // Egen finansiering per år (rowId='3', columnId=år)
    if (projektpart.egenFinansiering) {
      for (const egenFinansieringPerAr of projektpart.egenFinansiering) {
        this.addAndringsmarkeringar(
          egenFinansieringPerAr.andringsmarkeringar,
          changes,
          '3',
          egenFinansieringPerAr.ar.toString(),
        );
      }
    }

    // Egen finansiering totalt (rowId='3', columnId='total')
    if (projektpart.egenFinansieringTotalt) {
      this.addAndringsmarkeringar(
        projektpart.egenFinansieringTotalt.andringsmarkeringar,
        changes,
        '3',
        'total',
      );
    }

    // Total finansiering per år (rowId='total', columnId=år)
    if (projektpart.totalFinansiering) {
      for (const finansiering of projektpart.totalFinansiering) {
        this.addAndringsmarkeringar(
          finansiering.andringsmarkeringar,
          changes,
          'total',
          finansiering.ar.toString(),
        );
      }
    }

    // Bidrag Vinnova totalt (rowId='1', columnId='total')
    if (projektpart.bidragVinnovaTotalt) {
      this.addAndringsmarkeringar(
        projektpart.bidragVinnovaTotalt.andringsmarkeringar,
        changes,
        '1',
        'total',
      );
    }

    // Grand total finansiering (rowId='total', columnId='total')
    if (projektpart.totalFinansieringTotalt) {
      this.addAndringsmarkeringar(
        projektpart.totalFinansieringTotalt.andringsmarkeringar,
        changes,
        'total',
        'total',
      );
    }

    return changes;
  }
}
